import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const PLATFORM_OPTIONS = [
  { value: "meta", label: "Meta (Facebook/Instagram)" },
  { value: "google", label: "Google Ads" },
  { value: "tiktok", label: "TikTok Ads" },
  { value: "linkedin", label: "LinkedIn Ads" },
  { value: "twitter", label: "X (Twitter) Ads" },
  { value: "other", label: "Other" },
];

// Expected CSV columns (case-insensitive, flexible matching)
const COLUMN_MAP: Record<string, string> = {
  date: "spend_date",
  spend_date: "spend_date",
  day: "spend_date",
  spend: "spend",
  "spend ($)": "spend",
  cost: "spend",
  amount: "spend",
  amount_spent: "spend",
  "amount spent": "spend",
  impressions: "impressions",
  impr: "impressions",
  "impr.": "impressions",
  clicks: "clicks",
  link_clicks: "clicks",
  "link clicks": "clicks",
  campaign: "campaign_name",
  campaign_name: "campaign_name",
  "campaign name": "campaign_name",
  campaign_id: "campaign_id",
  "campaign id": "campaign_id",
  ad_set: "ad_set_name",
  ad_set_name: "ad_set_name",
  "ad set name": "ad_set_name",
  "ad set": "ad_set_name",
  adset_name: "ad_set_name",
  adset: "ad_set_name",
  ad_set_id: "ad_set_id",
  "ad set id": "ad_set_id",
  adset_id: "ad_set_id",
  ad_name: "ad_name",
  "ad name": "ad_name",
  ad_id: "ad_id",
  "ad id": "ad_id",
  platform: "platform",
};

interface ParsedRow {
  spend_date: string;
  spend_cents: number;
  impressions: number;
  clicks: number;
  campaign_name: string | null;
  campaign_id: string | null;
  ad_set_name: string | null;
  ad_set_id: string | null;
  ad_name: string | null;
  ad_id: string | null;
  platform: string;
  error?: string;
}

function parseCsvText(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseRow = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(parseRow);
  return { headers, rows };
}

function parseDate(val: string): string | null {
  // Try yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  // Try mm/dd/yyyy or m/d/yyyy
  const mdy = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    return `${mdy[3]}-${mdy[1].padStart(2, "0")}-${mdy[2].padStart(2, "0")}`;
  }
  // Try dd/mm/yyyy (fallback)
  const dmy = val.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  }
  return null;
}

interface AdSpendCsvImportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: () => void;
}

const AdSpendCsvImport = ({ open, onOpenChange, onImported }: AdSpendCsvImportProps) => {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [defaultPlatform, setDefaultPlatform] = useState("meta");
  const [parsed, setParsed] = useState<ParsedRow[]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [step, setStep] = useState<"upload" | "preview">("upload");
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [fileName, setFileName] = useState("");
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [checking, setChecking] = useState(false);
  const [overwriteDuplicates, setOverwriteDuplicates] = useState(false);
  const [existingDupKeys, setExistingDupKeys] = useState<Map<string, string[]>>(new Map());

  const reset = () => {
    setParsed([]);
    setColumnMapping({});
    setStep("upload");
    setConfirmOverwrite(false);
    setFileName("");
    setImporting(false);
    setDuplicateCount(0);
    setChecking(false);
    setOverwriteDuplicates(false);
    setExistingDupKeys(new Map());
    if (fileRef.current) fileRef.current.value = "";
  };

  const checkDuplicates = async (rows: ParsedRow[]): Promise<ParsedRow[]> => {
    const valid = rows.filter((r) => !r.error);
    if (valid.length === 0) return rows;

    const dates = [...new Set(valid.map((r) => r.spend_date))];
    const platforms = [...new Set(valid.map((r) => r.platform))];

    const { data: existing } = await supabase
      .from("ad_spend")
      .select("id, platform, spend_date, campaign_name")
      .in("spend_date", dates)
      .in("platform", platforms);

    if (!existing || existing.length === 0) return rows;

    // Group existing IDs by key for deletion during overwrite
    const keyToIds = new Map<string, string[]>();
    existing.forEach((e) => {
      const key = `${e.platform}|${e.spend_date}|${e.campaign_name || ""}`;
      keyToIds.set(key, [...(keyToIds.get(key) || []), e.id]);
    });
    setExistingDupKeys(keyToIds);

    const existingKeys = new Set(keyToIds.keys());

    return rows.map((r) => {
      if (r.error) return r;
      const key = `${r.platform}|${r.spend_date}|${r.campaign_name || ""}`;
      if (existingKeys.has(key)) {
        return { ...r, error: "Duplicate" };
      }
      return r;
    });
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const text = ev.target?.result as string;
      const { headers, rows } = parseCsvText(text);

      // Auto-map columns
      const mapping: Record<string, string> = {};
      headers.forEach((h, i) => {
        const normalized = h.toLowerCase().trim().replace(/['"]/g, "");
        if (COLUMN_MAP[normalized]) {
          mapping[i.toString()] = COLUMN_MAP[normalized];
        }
      });
      setColumnMapping(mapping);

      // Parse rows
      const parsed: ParsedRow[] = rows
        .filter((r) => r.some((c) => c.trim()))
        .map((cols) => {
          const get = (field: string) => {
            const idx = Object.entries(mapping).find(([, v]) => v === field)?.[0];
            return idx !== undefined ? cols[parseInt(idx)]?.trim() || "" : "";
          };

          const dateRaw = get("spend_date");
          const spendDate = dateRaw ? parseDate(dateRaw) : null;
          const spendRaw = get("spend").replace(/[$,]/g, "");
          const spendVal = parseFloat(spendRaw);
          const spendCents = isNaN(spendVal) ? 0 : Math.round(spendVal * 100);
          const impressions = parseInt(get("impressions").replace(/,/g, "")) || 0;
          const clicks = parseInt(get("clicks").replace(/,/g, "")) || 0;
          const platform = get("platform")?.toLowerCase() || defaultPlatform;

          let error: string | undefined;
          if (!spendDate) error = "Invalid date";
          else if (spendCents <= 0) error = "Invalid spend";

          return {
            spend_date: spendDate || "",
            spend_cents: spendCents,
            impressions,
            clicks,
            campaign_name: get("campaign_name") || null,
            campaign_id: get("campaign_id") || null,
            ad_set_name: get("ad_set_name") || null,
            ad_set_id: get("ad_set_id") || null,
            ad_name: get("ad_name") || null,
            ad_id: get("ad_id") || null,
            platform,
            error,
          };
        });

      setParsed(parsed);
      setStep("preview");

      // Check for duplicates against existing DB records
      setChecking(true);
      const checked = await checkDuplicates(parsed);
      setParsed(checked);
      setDuplicateCount(checked.filter((r) => r.error === "Duplicate").length);
      setChecking(false);
    };
    reader.readAsText(file);
  };

  const validRows = parsed.filter((r) => !r.error);
  const errorRows = parsed.filter((r) => r.error);
  const dupRows = parsed.filter((r) => r.error === "Duplicate");

  const importableRows = overwriteDuplicates
    ? parsed.filter((r) => !r.error || r.error === "Duplicate")
    : validRows;

  const handleImport = async () => {
    if (importableRows.length === 0) return;
    setImporting(true);

    // If overwriting, delete existing duplicates first
    if (overwriteDuplicates && dupRows.length > 0) {
      const idsToDelete: string[] = [];
      dupRows.forEach((r) => {
        const key = `${r.platform}|${r.spend_date}|${r.campaign_name || ""}`;
        const ids = existingDupKeys.get(key);
        if (ids) idsToDelete.push(...ids);
      });

      if (idsToDelete.length > 0) {
        const { error } = await supabase
          .from("ad_spend")
          .delete()
          .in("id", idsToDelete);
        if (error) {
          toast({ title: "Failed to remove duplicates", description: error.message, variant: "destructive" });
          setImporting(false);
          return;
        }
      }
    }

    // Insert in batches of 50
    const batchSize = 50;
    let inserted = 0;
    const rowsToInsert = importableRows;
    for (let i = 0; i < rowsToInsert.length; i += batchSize) {
      const batch = rowsToInsert.slice(i, i + batchSize).map((r) => ({
        platform: r.platform,
        campaign_name: r.campaign_name,
        campaign_id: r.campaign_id,
        ad_set_name: r.ad_set_name,
        ad_set_id: r.ad_set_id,
        ad_name: r.ad_name,
        ad_id: r.ad_id,
        spend_date: r.spend_date,
        spend_cents: r.spend_cents,
        impressions: r.impressions,
        clicks: r.clicks,
      }));

      const { error } = await supabase.from("ad_spend").insert(batch);
      if (error) {
        toast({ title: "Import error", description: error.message, variant: "destructive" });
        setImporting(false);
        return;
      }
      inserted += batch.length;
    }

    const skipped = parsed.length - inserted;
    toast({ title: `Imported ${inserted} rows`, description: skipped > 0 ? `${skipped} rows skipped.` : undefined });
    setImporting(false);
    onOpenChange(false);
    reset();
    onImported();
  };

  const downloadTemplate = () => {
    const csv = [
      "Date,Platform,Campaign Name,Campaign ID,Ad Set Name,Ad Set ID,Ad Name,Ad ID,Spend ($),Impressions,Clicks",
      "2026-04-01,meta,Spring Promo,123456,Lookalike 1%,789,Ad Variant A,101,150.50,25000,320",
      "2026-04-01,google,Brand Search,654321,Exact Match,987,Headline Test,202,85.00,12000,180",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ad-spend-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet size={18} /> Import Ad Spend from CSV
          </DialogTitle>
          <DialogDescription>
            Upload a CSV export from Meta, Google, or TikTok Ads Manager. Columns are auto-mapped.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4 py-2">
            <div>
              <Label>Default Platform (used if CSV has no platform column)</Label>
              <Select value={defaultPlatform} onValueChange={setDefaultPlatform}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORM_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              <Upload size={32} className="mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm font-medium">Click to upload CSV file</p>
              <p className="text-xs text-muted-foreground mt-1">
                Supports Meta, Google Ads, and TikTok Ads Manager exports
              </p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleFile}
              />
            </div>

            <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadTemplate}>
              <Download size={14} /> Download Template CSV
            </Button>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="outline">{fileName}</Badge>
              {checking ? (
                <span className="text-sm text-muted-foreground">Checking for duplicates…</span>
              ) : (
                <div className="flex gap-2 text-sm">
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle2 size={14} /> {validRows.length} valid
                  </span>
                  {dupRows.length > 0 && (
                    <span className="flex items-center gap-1 text-amber-600">
                      <AlertTriangle size={14} /> {dupRows.length} duplicates
                    </span>
                  )}
                  {errorRows.length > dupRows.length && (
                    <span className="flex items-center gap-1 text-destructive">
                      <AlertTriangle size={14} /> {errorRows.length - dupRows.length} errors
                    </span>
                  )}
                </div>
              )}
            </div>

            {duplicateCount > 0 && !checking && (
              <div className="text-sm bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                  <span>
                    <strong>{duplicateCount} row{duplicateCount > 1 ? "s" : ""}</strong> already exist in the database (same platform + date + campaign).
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-6">
                  <Checkbox
                    id="overwrite-duplicates"
                    checked={overwriteDuplicates}
                    onCheckedChange={(checked) => setOverwriteDuplicates(checked === true)}
                  />
                  <label htmlFor="overwrite-duplicates" className="text-sm cursor-pointer">
                    Overwrite existing entries with CSV data
                  </label>
                </div>
              </div>
            )}

            <div className="max-h-[300px] overflow-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Ad Set</TableHead>
                    <TableHead className="text-right">Spend</TableHead>
                    <TableHead className="text-right">Impr.</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsed.slice(0, 100).map((r, i) => (
                    <TableRow key={i} className={r.error ? "bg-destructive/5" : ""}>
                      <TableCell className="text-muted-foreground text-xs">{i + 1}</TableCell>
                      <TableCell className="text-sm">{r.spend_date || "—"}</TableCell>
                      <TableCell><Badge variant="secondary" className="text-xs">{r.platform}</Badge></TableCell>
                      <TableCell className="text-sm max-w-[120px] truncate">{r.campaign_name || "—"}</TableCell>
                      <TableCell className="text-sm max-w-[120px] truncate">{r.ad_set_name || "—"}</TableCell>
                      <TableCell className="text-right text-sm">${(r.spend_cents / 100).toFixed(2)}</TableCell>
                      <TableCell className="text-right text-sm">{r.impressions.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-sm">{r.clicks.toLocaleString()}</TableCell>
                      <TableCell>
                        {r.error ? (
                          <Badge variant="destructive" className="text-xs">{r.error}</Badge>
                        ) : (
                          <CheckCircle2 size={14} className="text-green-600" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {parsed.length > 100 && (
              <p className="text-xs text-muted-foreground">Showing first 100 of {parsed.length} rows.</p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === "preview" && (
            <Button variant="outline" onClick={reset}>Back</Button>
          )}
          <Button variant="outline" onClick={() => { onOpenChange(false); reset(); }}>Cancel</Button>
          {step === "preview" && !confirmOverwrite && (
            <Button
              onClick={() => {
                if (overwriteDuplicates && dupRows.length > 0) {
                  setConfirmOverwrite(true);
                } else {
                  handleImport();
                }
              }}
              disabled={importing || importableRows.length === 0}
              variant={overwriteDuplicates ? "destructive" : "default"}
            >
              {importing
                ? "Importing…"
                : overwriteDuplicates
                  ? `Overwrite & Import ${importableRows.length} Rows`
                  : `Import ${importableRows.length} Rows`}
            </Button>
          )}
          {confirmOverwrite && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-destructive font-medium">
                This will delete {dupRows.length} existing record{dupRows.length > 1 ? "s" : ""}. Are you sure?
              </span>
              <Button variant="outline" size="sm" onClick={() => setConfirmOverwrite(false)}>
                No, go back
              </Button>
              <Button variant="destructive" size="sm" onClick={() => { setConfirmOverwrite(false); handleImport(); }} disabled={importing}>
                {importing ? "Importing…" : "Yes, overwrite"}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdSpendCsvImport;
